/**
 * Video Configuration
 * Maps S3-hosted narration videos to game stages and narrative points
 */

const S3_BASE = 'https://cesim-game-assets.s3.amazonaws.com/videos';

export interface VideoCondition {
  questionId: string;
  answers: string[];
  playerRole?: string;
}

export interface VideoConfig {
  url: string;
  name: string;
  duration: string;
  timing: 'onEntry' | 'afterQuestion' | 'onExit';
  narrativeIndex?: number;
  videoOnly?: boolean;
  condition?: VideoCondition;
}

export const STARTUP_VIDEO = `${S3_BASE}/pre-game/pre-game_startup-centauri.mp4`;

export const STAGE_VIDEOS: Record<number, VideoConfig[]> = {
  0: [
    {
      url: `${S3_BASE}/stage-0/stage-0_introvideo-v9.mp4`,
      name: 'IntroVideo V9',
      duration: '6:47',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
    },
    {
      url: `${S3_BASE}/stage-0/stage-0_so-messagemship-1.mp4`,
      name: 'SO MessageMship 1',
      duration: '1:18',
      timing: 'onEntry',
      narrativeIndex: 2,
    },
    {
      url: `${S3_BASE}/stage-0/stage-0_so-messagemship-2.mp4`,
      name: 'SO MessageMship 2',
      duration: '0:23',
      timing: 'onEntry',
      narrativeIndex: 4,
    },
    {
      url: `${S3_BASE}/stage-0/stage-0_so-onboard-convo-1.mp4`,
      name: 'SO Onboard Convo 1',
      duration: '0:31',
      timing: 'afterQuestion',
      narrativeIndex: undefined,
    },
    {
      url: `${S3_BASE}/stage-0/stage-0_so-combined.mp4`,
      name: 'SO Combined',
      duration: '1:22',
      timing: 'onExit',
      narrativeIndex: 0,
      videoOnly: true,
    },
  ],
  1: [
    {
      url: `${S3_BASE}/stage-1/stage-1_s1-g1-1.mp4`,
      name: 'S1 G1 1 - Immediate Teleport',
      duration: '1:04',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
      condition: { questionId: 'Q5', answers: ['A'] },
    },
    {
      url: `${S3_BASE}/stage-1/stage-1_s1-g1-2.mp4`,
      name: 'S1 G1 2 - Delayed Teleport',
      duration: '1:10',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
      condition: { questionId: 'Q5', answers: ['B', 'C'] },
    },
    {
      url: `${S3_BASE}/stage-1/stage-1_s1-messagemship-1.mp4`,
      name: 'S1 MessageMship 1',
      duration: '1:16',
      timing: 'afterQuestion',
      narrativeIndex: undefined,
      condition: { questionId: 'Q6', answers: ['A'] },
    },
    {
      url: `${S3_BASE}/stage-1/stage-1_s1-messagemship-2.mp4`,
      name: 'S1 MessageMship 2',
      duration: '0:37',
      timing: 'onExit',
      narrativeIndex: 0,
      videoOnly: true,
      condition: { questionId: 'Q6', answers: ['B', 'C'] },
    },
  ],
  2: [
    {
      url: `${S3_BASE}/stage-2/stage-2_s2-g1-1.mp4`,
      name: 'S2 G1 1 - Cave Descent',
      duration: '2:01',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
    },
    {
      url: `${S3_BASE}/stage-2/stage-2_s2-doc-1.mp4`,
      name: 'S2 Doc 1',
      duration: '0:16',
      timing: 'afterQuestion',
      narrativeIndex: undefined,
      condition: { questionId: 'Q7A', answers: ['B'] },
    },
    {
      url: `${S3_BASE}/stage-2/stage-2_s2-doc-2.mp4`,
      name: 'S2 Doc 2',
      duration: '0:07',
      timing: 'afterQuestion',
      narrativeIndex: undefined,
    },
    {
      url: `${S3_BASE}/stage-2/stage-2_s2-doc-3.mp4`,
      name: 'S2 Doc 3',
      duration: '0:21',
      timing: 'afterQuestion',
      narrativeIndex: undefined,
      condition: { questionId: 'Q7A', answers: ['A'] },
    },
    {
      url: `${S3_BASE}/stage-2/stage-2_s2-doc-4.mp4`,
      name: 'S2 Doc 4 / GEE returns',
      duration: '0:16',
      timing: 'afterQuestion',
      narrativeIndex: undefined,
    },
    {
      url: `${S3_BASE}/stage-2/stage-2_s2-gee-1.mp4`,
      name: 'S2 Narrator at end',
      duration: '0:23',
      timing: 'onExit',
      narrativeIndex: 0,
      videoOnly: true,
    },
  ],
  3: [
    {
      url: `${S3_BASE}/stage-3/stage-3_stage-3-video-1.mp4`,
      name: 'Stage 3 Video 1 - Descent',
      duration: '1:09',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
    },
    {
      url: `${S3_BASE}/stage-3/stage-3_stage-3-video-2.mp4`,
      name: 'Stage 3 Video 2 - Ocean Floor',
      duration: '1:25',
      timing: 'onEntry',
      narrativeIndex: 1,
      videoOnly: true,
    },
  ],
  4: [
    {
      url: `${S3_BASE}/stage-4/stage-4_stage-4-video-1.mp4`,
      name: 'Stage 4 Video 1 - Mission Day',
      duration: '1:00',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
    },
    {
      url: `${S3_BASE}/stage-4/stage-4_s4-onboardconvo-1.mp4`,
      name: 'S4 OnboardConvo 1 - GEE Missing',
      duration: '0:32',
      timing: 'onEntry',
      narrativeIndex: 1,
    },
  ],
  5: [
    {
      url: `${S3_BASE}/stage-5/stage-5_s5a-combined.mp4`,
      name: 'S5A Combined - Fuel Alert',
      duration: '1:17',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
      condition: { questionId: 'Q7', answers: ['A', 'C'], playerRole: 'FIXER' },
    },
    {
      url: `${S3_BASE}/stage-5/stage-5_s5b-combined.mp4`,
      name: 'S5B Combined - Fuel Alert',
      duration: '1:13',
      timing: 'onEntry',
      narrativeIndex: 0,
      videoOnly: true,
      condition: { questionId: 'Q7', answers: ['B'], playerRole: 'FIXER' },
    },
    {
      url: `${S3_BASE}/stage-5/stage-5_s5a-messagemship-2.mp4`,
      name: 'S5A MessageMship 2 - Ascension Process',
      duration: '0:27',
      timing: 'onEntry',
      narrativeIndex: 1,
      condition: { questionId: 'Q7', answers: ['A', 'C'], playerRole: 'FIXER' },
    },
    {
      url: `${S3_BASE}/stage-5/stage-5_s5-onboardconvo-1.mp4`,
      name: 'S5 OnboardConvo 1 - Ascension Process',
      duration: '0:33',
      timing: 'onEntry',
      narrativeIndex: 1,
      condition: { questionId: 'Q7', answers: ['B'], playerRole: 'FIXER' },
    },
  ],
};
